import { useMemo } from 'react';

import { invariant } from '@spotify/confidence-core-react';
import { StatsDataFragment } from '@spotify/confidence-graphql';
import {
  useIsStatsModuleStrategySequentialV2,
  useWorkflowInstanceContext,
} from '@spotify/plugin-confidence-workflows-react';

import { convertAnalysisResults } from '../helpers';
import { AnalysisResult, StatsData2 } from '../types';

type UseStatsDataOptions = {
  exposureFilter?: string;
};

const emptyAnalysisResult: AnalysisResult = {
  results: [],
  annotations: {},
  recommendations: [],
};

const hasCalculatedResults = (analysisResult: AnalysisResult): boolean =>
  analysisResult.results.some(hypothesisResult =>
    hypothesisResult.result.some(comparisonResult =>
      Boolean(comparisonResult.lastResult),
    ),
  );

const selectAnalysisResult = (
  analysisResults: AnalysisResult[],
  exposureFilter: string,
): AnalysisResult =>
  analysisResults.length === 1
    ? analysisResults[0]
    : (analysisResults.find(
        result => result.exposureFilter === exposureFilter,
      ) ?? emptyAnalysisResult);

export const useStatsData = ({
  exposureFilter = 'base',
}: UseStatsDataOptions = {}): StatsData2 => {
  const { hasLaunched, isLive, moduleData2, state } =
    useWorkflowInstanceContext();

  const statsData: StatsDataFragment | undefined | null = moduleData2?.stats;
  invariant(statsData, 'useStatsData: stats data is required');

  const analysisResults = useMemo(
    () => convertAnalysisResults(statsData.analysisResults),
    [statsData.analysisResults],
  );
  const selectedAnalysisResult = useMemo(
    () => selectAnalysisResult(analysisResults, exposureFilter),
    [analysisResults, exposureFilter],
  );
  const isSequential = useIsStatsModuleStrategySequentialV2(statsData);
  const isFixedHorizon = !isSequential;
  const hasResults = hasCalculatedResults(selectedAnalysisResult);
  const isActiveWorkflow = state === 'active';
  const isArchivedWithResults = state === 'archived' && hasResults;
  const areResultsExpected =
    isActiveWorkflow ||
    isArchivedWithResults ||
    (hasLaunched && (isSequential || (isFixedHorizon && !isLive)));
  return {
    analysisResults,
    selectedAnalysisResult,
    isSequential,
    isFixedHorizon,
    areResultsExpected,
    hasResultsToShow: areResultsExpected && hasResults,
  };
};
